import { Link } from 'react-router-dom';
import { OnboardingState } from './types';

interface StepSuccessProps {
  state: OnboardingState;
}

const NEXT_ACTIONS = [
  {
    id: 'invite',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
    title: 'Invite an interviewer',
    description: 'Get another perspective on this candidate',
    benefit: 'This gets clearer when multiple evaluators weigh in.',
  },
  {
    id: 'candidate',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
      </svg>
    ),
    title: 'Add another candidate',
    description: 'Compare candidates side-by-side',
    benefit: 'This gets clearer when you can compare signal.',
  },
  {
    id: 'ats',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
      </svg>
    ),
    title: 'Connect your ATS',
    description: 'Sync candidates automatically',
    benefit: 'This gets clearer with a continuous flow of candidates.',
  },
  {
    id: 'bar-raiser',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
    title: 'Enable bar-raiser mode',
    description: 'Require final approval before offers',
    benefit: 'This gets clearer with an independent decision gate.',
  },
];

export function StepSuccess({ state }: StepSuccessProps) {
  const decisionText = {
    lean_yes: 'leaning yes',
    lean_no: 'leaning no',
    need_signal: 'need more signal',
  };

  const decisionEmoji = {
    lean_yes: '👍',
    lean_no: '👎',
    need_signal: '🤔',
  };

  const candidateName = state.candidate?.name || 'your candidate';
  const roleTitle = state.role?.title || 'the role';

  return (
    <div className="animate-fade-in min-h-screen flex flex-col relative overflow-hidden">
      {/* Ambient background - matching GetStartedPage */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-white to-brand-50/30" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-emerald-100/40 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-brand-50/50 via-transparent to-transparent rounded-full blur-3xl" />
        {/* Celebration particles effect */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full opacity-20"
              style={{
                backgroundColor: ['#10b981', '#8b5cf6', '#f59e0b', '#06b6d4'][i % 4],
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-surface-200/60 bg-white/70 backdrop-blur-xl py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/images/logo.png" alt="JumpstoneHR" className="w-8 h-8 transition-transform group-hover:scale-105" />
            <span className="font-display font-semibold text-lg text-surface-900">Jumpstone</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        {/* Success icon with animation */}
        <div className="relative mb-10">
          <div className="absolute inset-0 bg-emerald-400/20 rounded-full blur-2xl scale-[2] animate-pulse" />
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30 ring-8 ring-emerald-100/50">
            <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
        </div>

        {/* Message */}
        <h1 className="text-4xl sm:text-5xl font-display font-bold text-surface-900 mb-5 text-center">
          You've got signal.
        </h1>
        
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-surface-200 p-6 mb-6 max-w-lg text-center shadow-sm">
          <p className="text-lg text-surface-600">
            You evaluated <span className="font-semibold text-surface-900">{candidateName}</span> for{' '}
            <span className="font-semibold text-surface-900">{roleTitle}</span>
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-100">
            <span className="text-xl">{state.decision?.lean ? decisionEmoji[state.decision.lean] : ''}</span>
            <span className="font-medium text-surface-700">
              You're {state.decision?.lean ? decisionText[state.decision.lean] : ''}
            </span>
          </div>
        </div>
        
        <p className="text-surface-500 text-center max-w-md mb-14">
          This is how Jumpstone helps teams make clearer, more confident hiring decisions—every time.
        </p>

        {/* Next actions */}
        <div className="w-full max-w-2xl">
          <h2 className="text-sm font-semibold text-surface-500 uppercase tracking-wider mb-5 text-center">
            What's next
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {NEXT_ACTIONS.map((action, index) => (
              <button
                key={action.id}
                className="group p-5 bg-white/80 backdrop-blur-sm rounded-2xl border border-surface-200 hover:border-brand-300 hover:shadow-xl hover:shadow-brand-500/5 transition-all duration-300 text-left"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-50 to-brand-100 text-brand-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    {action.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-surface-900 group-hover:text-brand-700 transition-colors">
                      {action.title}
                    </p>
                    <p className="text-sm text-surface-500 mb-2">{action.description}</p>
                    <p className="text-xs text-brand-600 font-medium italic">{action.benefit}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-surface-900 text-white hover:bg-surface-800 shadow-xl shadow-surface-900/20 text-base px-10 py-4 rounded-xl font-semibold transition-all duration-200 hover:scale-[1.02]"
          >
            Go to Dashboard
            <svg className="w-5 h-5 transition-transform group-hover:translate-x-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      </main>

      {/* Inline keyframes for particle animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </div>
  );
}
